<template>
  <EnterViewportDetector class="m-auto block size-full">
    <template #default="{ inViewport }">
      <RevCarousel
        :alternative-button-label="i18n(translations.alternativeButtonLabel)"
        :alternative-next="i18n(translations.alternativeNext)"
        :alternative-previous="i18n(translations.alternativePrevious)"
        :autoplay="inViewport ? FF_CMS_SLIDER_AUTOPLAY === true : false"
        class="relative m-auto block"
        :class="{
          'w-full lg:max-w-[125rem]': isFullWidth,
          'md:px-24 lg:max-w-[1120px] lg:px-0': !isFullWidth,
        }"
        current-index-id="cms-carousel"
        :tracking="trackingData"
        @slide-impression="handleSlideImpression"
      >
        <component
          :is="slide.tag"
          v-for="(slide, index) in sliderSlides"
          :key="slide.key"
          class="h-full w-full"
          :rel="slide.link?.rel"
          :target="slide.link?.target"
          :to="slide.link?.href"
          @click="slide.handleSlideClick"
        >
          <SliderWrapper v-bind="slide" :is-full-width>
            <ImageRenderer
              :alt="slide.image?.alt"
              class="w-full"
              :fetchpriority="index === 0 ? 'high' : 'low'"
              :height="slide.image?.height"
              :height-mobile="slide.image?.heightMobile"
              :loading="slide.isLoading"
              :media-max-width="MediaMaxWidth.SM"
              :src="slide.image?.src"
              :src-mobile="slide.image?.srcMobile"
              :width="slide.image?.width"
              :width-mobile="slide.image?.widthMobile"
            />
          </SliderWrapper>
        </component>
      </RevCarousel>
    </template>
  </EnterViewportDetector>
</template>

<script setup lang="ts">
import { useRoute, useRuntimeConfig } from '#imports'
import { computed } from 'vue'

import type { SliderApiProps } from '@backmarket/http-api/src/api-specs-content/models/slider'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import type { CarouselData } from '@backmarket/nuxt-module-tracking/types'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevCarousel } from '@ds/components/Carousel'

import {
  Loading,
  MediaMaxWidth,
} from '@backmarket/nuxt-layer-cms/ImageRenderer.constants'
import ImageRenderer from '@backmarket/nuxt-layer-cms/ImageRenderer.vue'
import type { BlockProps } from '@backmarket/nuxt-layer-cms/models/content-block'

import EnterViewportDetector from '../../shared-components/EnterViewportDetector/EnterViewportDetector.vue'

import translations from './Slider.translations'
import SliderWrapper from './SliderWrapper.vue'

const i18n = useI18n()
const route = useRoute()
const { trackCarouselClick, trackSelectPromotion, trackViewPromotion } =
  useTracking()
const { FF_CMS_SLIDER_AUTOPLAY } = useRuntimeConfig().public

export type SliderBlockProps = SliderApiProps & BlockProps
const props = withDefaults(defineProps<SliderBlockProps>(), {
  isFullWidth: true,
})

// This event is common to every CMS block in order to hide the block containers
// on demand
defineEmits(['error', 'reset-error', 'loaded'])

type PromotionParams = { creativeName: string; creativeSlot: string }

const trackSlideClick =
  ({ creativeName, creativeSlot }: PromotionParams) =>
  () => {
    trackCarouselClick({
      ...(props.tracking || {}),
      creative: creativeName,
      name: String(route.name),
      position: creativeSlot,
    })
    trackSelectPromotion({ creativeName, creativeSlot })
  }

const sliderSlides = computed(() => {
  return props.slides.map((slide, index) => {
    const hasLink = !isEmpty(slide.link)
    const creativeName = slide.technicalName || ''
    const creativeSlot = `${String(route.name)}_${index + 1}`

    return {
      ...slide,
      tag: hasLink ? RevButtonBase : 'div',
      key: slide.image?.src,
      target: slide.link?.target,
      isLoading: index === 0 ? Loading.Eager : Loading.Lazy,
      hasLink,
      creativeName,
      creativeSlot,
      handleSlideClick: hasLink
        ? trackSlideClick({ creativeName, creativeSlot })
        : undefined,
    }
  })
})

function handleSlideImpression({ index }: { index: number }) {
  const slide = sliderSlides.value[index]

  if (slide?.hasLink) {
    trackViewPromotion({
      creativeName: slide.creativeName,
      creativeSlot: slide.creativeSlot,
    })
  }
}

const trackingData = computed<Omit<CarouselData, 'currentSlide'>>(() => {
  return {
    ...(props.tracking || {}),
    name: String(route.name),
    creatives: props.slides.map((slide) => ({
      creative: slide.technicalName || '',
    })),
  }
})
</script>
